import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from './lib/api.js';
import type { OutletSubscriptionInfo } from './lib/subscription.js';
import { tryAutoLogin, signOut as firebaseSignOut, getIdToken, startTokenRefreshListener, isEmailSignInLink } from './lib/firebase.js';
import { APP_DISPLAY_NAME, IS_DEMO } from './lib/demo.js';
import { siteManifestUrl } from './lib/site-config.js';
import './components/landing-screen.js';
import './components/app-shell.js';
import './components/stock-search.js';

// Environment label: on demo hosts the tab title (and the installable PWA
// manifest) read "DEMO" so testers never confuse it with production.
document.title = APP_DISPLAY_NAME;

// The install name on a phone comes from the manifest, which vite-plugin-pwa
// bakes in at BUILD time. A deployment can point at its own manifest from
// /site-config.js (`manifestUrl` / `demoManifestUrl`), so ppos.my installs as
// "PPOS" while pharmapos.com keeps "PharmaPOS" — same build.
const manifestHref = siteManifestUrl() ?? (IS_DEMO ? '/manifest-demo.webmanifest' : null);
if (manifestHref) {
  const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (manifestLink) manifestLink.setAttribute('href', manifestHref);
}

// iOS ignores the web manifest for the home-screen name — it reads this meta.
document
  .querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]')
  ?.setAttribute('content', APP_DISPLAY_NAME);

@customElement('pharma-pos-app')
export class PharmaPosApp extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      min-height: 100dvh;
    }
    .loading-screen {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100dvh;
      gap: 1rem;
      color: var(--color-text-secondary);
    }
    .spinner {
      width: 32px;
      height: 32px;
      border: 3px solid var(--color-border);
      border-top-color: var(--color-primary);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;

  @state() private _isLoggedIn = false;
  @state() private _checkingSession = true;
  @state() private _sessionKey = '';
  @state() private _email = '';
  @state() private _ownedDBs: Array<{
    code: string;
    name: string;
    masterDB: string;
    transDB: string;
    hostAddress: string;
    hostPort: number;
    hostName: string;
    outletName: string;
    accessAs: string[];
    renewalOnly?: boolean;
    expiredReasons?: string[];
    subscription?: OutletSubscriptionInfo;
  }> = [];

  async connectedCallback() {
    super.connectedCallback();

    // Start proactive Firebase ID token refresh listener.
    // This keeps a cached token up-to-date so API calls never send a stale one.
    startTokenRefreshListener();

    // The API client performs silent re-logins when the gateway session dies
    // (gateway restarted, session record expired) — mirror the fresh session
    // key into reactive state + the sessionStorage cache.
    window.addEventListener('ppos-session-refreshed', this._onSessionRefreshed);

    // Service Worker update: auto-reload when new version activates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
    }

    // An email sign-in link must be completed by the landing screen — never
    // auto-skip past it (the link may target a different account than a
    // persisted Firebase session on this browser).
    if (isEmailSignInLink()) {
      this._checkingSession = false;
      return;
    }

    // 1. Fast path: restore from sessionStorage (same browser session)
    const saved = sessionStorage.getItem('ppos_session');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        this._sessionKey = data.sessionKey;
        this._email = data.email;
        this._ownedDBs = data.ownedDBs || [];
        this._isLoggedIn = true;
        this._checkingSession = false;
        api.setSessionKey(data.sessionKey);

        // Re-establish server-side sessions in the background
        // (they may have been lost due to gateway restart)
        this._reestablishSessions();
        return;
      } catch {
        sessionStorage.removeItem('ppos_session');
      }
    }

    // 2. Try Firebase auto-login (survives browser restarts)
    try {
      const idToken = await tryAutoLogin();
      if (idToken) {
        const result = await api.login(idToken);
        if (result.success) {
          this._sessionKey = result.data.sessionKey;
          this._email = result.data.email;
          this._ownedDBs = result.data.ownedDBs || [];
          this._isLoggedIn = true;
          api.setSessionKey(result.data.sessionKey);
          sessionStorage.setItem('ppos_session', JSON.stringify({
            sessionKey: result.data.sessionKey,
            email: result.data.email,
            ownedDBs: result.data.ownedDBs,
          }));
        }
      }
    } catch {
      // Auto-login failed — user needs to sign in manually
    }

    this._checkingSession = false;
  }

  /**
   * Fire-and-forget: re-establish server-side sessions after page refresh.
   * Sessions may have been lost on the gateway (restart, in-memory store
   * cleared) — the fresh session key returned by /auth/login MUST replace the
   * stored one, otherwise every call keeps presenting the key from the
   * previous gateway run until the user logs out and back in.
   */
  private async _reestablishSessions() {
    try {
      const idToken = await getIdToken();
      if (!idToken) return;
      const result = await api.login(idToken);
      if (result?.success && result.data?.sessionKey) {
        this._sessionKey = result.data.sessionKey;
        if (result.data.email) this._email = result.data.email;
        if (result.data.ownedDBs) this._ownedDBs = result.data.ownedDBs;
        api.setSessionKey(result.data.sessionKey);
        sessionStorage.setItem('ppos_session', JSON.stringify({
          sessionKey: result.data.sessionKey,
          email: this._email,
          ownedDBs: this._ownedDBs,
        }));
      }
    } catch {
      // Silently ignore — the API client falls back to silent re-login on 401.
    }
  }

  private _handleLogin(e: CustomEvent) {
    const { sessionKey, email, ownedDBs } = e.detail;
    this._sessionKey = sessionKey;
    this._email = email;
    this._ownedDBs = ownedDBs || [];
    this._isLoggedIn = true;
    api.setSessionKey(sessionKey);
    sessionStorage.setItem('ppos_session', JSON.stringify({ sessionKey, email, ownedDBs }));
  }

  /** Fresh session key minted by a silent re-login inside the API client. */
  private _onSessionRefreshed = (e: Event) => {
    const detail = (e as CustomEvent).detail || {};
    if (!detail.sessionKey) return;
    this._sessionKey = detail.sessionKey;
    if (detail.email) this._email = detail.email;
    if (Array.isArray(detail.ownedDBs) && detail.ownedDBs.length) {
      this._ownedDBs = detail.ownedDBs;
    }
    try {
      const saved = sessionStorage.getItem('ppos_session');
      if (saved) {
        const data = JSON.parse(saved);
        data.sessionKey = detail.sessionKey;
        if (detail.email) data.email = detail.email;
        if (Array.isArray(detail.ownedDBs) && detail.ownedDBs.length) data.ownedDBs = detail.ownedDBs;
        sessionStorage.setItem('ppos_session', JSON.stringify(data));
      }
    } catch {
      /* ignore */
    }
  };

  private async _handleLogout() {
    this._sessionKey = '';
    this._email = '';
    this._ownedDBs = [];
    this._isLoggedIn = false;
    sessionStorage.removeItem('ppos_session');
    // Also sign out of Firebase so auto-login won't trigger on next visit
    try { await firebaseSignOut(); } catch { /* ignore */ }
  }

  /**
   * The shell re-resolves owned stores when a server comes online (so outlets
   * that were offline at login appear without logging out). Keep the parent's
   * copy + the sessionStorage cache in sync.
   */
  private _handleOwnedDBsChanged(e: CustomEvent) {
    const ownedDBs = e.detail?.ownedDBs;
    if (!Array.isArray(ownedDBs)) return;
    this._ownedDBs = ownedDBs;
    try {
      const saved = sessionStorage.getItem('ppos_session');
      if (saved) {
        const data = JSON.parse(saved);
        data.ownedDBs = ownedDBs;
        sessionStorage.setItem('ppos_session', JSON.stringify(data));
      }
    } catch {
      /* ignore */
    }
  }

  render() {
    if (this._checkingSession) {
      return html`
        <div class="loading-screen">
          <div class="spinner"></div>
          <span>Checking session&hellip;</span>
        </div>
      `;
    }
    if (!this._isLoggedIn) {
      return html`<landing-screen @login-success=${this._handleLogin}></landing-screen>`;
    }
    return html`
      <app-shell
        .email=${this._email}
        .sessionKey=${this._sessionKey}
        .ownedDBs=${this._ownedDBs}
        @owned-dbs-changed=${this._handleOwnedDBsChanged}
        @logout=${this._handleLogout}
      ></app-shell>
    `;
  }
}
